// day profile from meter data (CSV / Excel): time + meter reading or consumption -> 24 hourly mean values
const numOf = (v) => {
  if (typeof v === 'number') return v;
  let s = String(v ?? '').trim().replace(/\s/g, '');
  if (!/^-?[\d.,]+$/.test(s)) return null;
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.'); else s = s.replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
};
// time cell -> { t: ms timestamp } or { h: hour of day } or null
const timeOf = (v) => {
  if (v instanceof Date && !isNaN(v)) return { t: v.getTime() };
  if (typeof v === 'number') { if (v > 20000 && v < 80000) return { t: Math.round((v - 25569) * 864e5) }; if (Number.isInteger(v) && v >= 0 && v <= 24) return { h: v % 24 }; return null; }
  const s = String(v ?? '').trim();
  let m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return { t: new Date(+(m[3].length === 2 ? `20${m[3]}` : m[3]), +m[2] - 1, +m[1], +m[4], +m[5], +(m[6] || 0)).getTime() };
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return { t: new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)).getTime() };
  m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m && +m[1] <= 24) return { h: +m[1] % 24 };
  return null;
};

async function rowsOf(file) {
  if (/\.(xlsx|xls|ods)$/i.test(file.name)) {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });
    return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true });
  }
  const txt = await file.text(), lines = txt.split(/\r?\n/).filter((l) => l.trim());
  const sep = [';', '\t', ','].find((c) => lines.slice(0, 5).every((l) => l.includes(c))) || ';';
  return lines.map((l) => l.split(sep).map((c) => c.replace(/^"|"$/g, '')));
}

// rows -> [{ t | h, v }] plus the header text (for units)
export async function readSeries(file) {
  const rows = await rowsOf(file), pts = [];
  rows.forEach((r) => {
    let tm = null, val = null;
    for (let i = 0; i < r.length; i++) {
      if (!tm) { const x = timeOf(r[i]); if (x) { tm = x; continue; } }
      if (tm && val == null) { const n = numOf(r[i]); if (n != null) val = n; }
    }
    if (tm && val != null) pts.push({ ...tm, v: val });
  });
  const head = rows.slice(0, 3).map((r) => r.join(' ')).join(' ');
  return { pts, head };
}

const isWe = (t) => { const d = new Date(t).getDay(); return d === 0 || d === 6; };
const fill24 = (sum, w) => {
  const prof = sum.map((s, h) => (w[h] ? s / w[h] : null)), miss = prof.filter((v) => v == null).length;
  if (miss > 8) return null;
  return prof.map((v, h) => { if (v != null) return v; let a = 1; while (prof[(h - a + 24) % 24] == null) a++; let b = 1; while (prof[(h + b) % 24] == null) b++; return (prof[(h - a + 24) % 24] * b + prof[(h + b) % 24] * a) / (a + b); }).map((v) => +Math.max(0, v).toFixed(4));
};

// meter data -> day profiles: workday, weekend (Sat/Sun) and their volume ratio, when the data covers both
export async function profileFromFile(file) {
  const { pts } = await readSeries(file);
  if (pts.length < 6) throw new Error('few');
  const B = { all: [Array(24).fill(0), Array(24).fill(0)], wd: [Array(24).fill(0), Array(24).fill(0)], we: [Array(24).fill(0), Array(24).fill(0)] };
  const add = (k, h, v, w) => { B[k][0][h] += v; B[k][1][h] += w; };
  let cumulative = false, days = 0, nWe = 0;
  if (pts.every((p) => p.h != null)) pts.forEach((p) => add('all', p.h, p.v, 1));
  else {
    const P = pts.filter((p) => p.t != null).sort((a, b) => a.t - b.t), up = P.slice(1).filter((p, i) => p.v >= P[i].v).length;
    cumulative = up >= 0.9 * (P.length - 1) && P[P.length - 1].v > P[0].v;
    days = Math.max(1, Math.round((P[P.length - 1].t - P[0].t) / 864e5));
    for (let i = 0; i < P.length - (cumulative ? 1 : 0); i++) {
      const h = new Date(P[i].t).getHours(), k = isWe(P[i].t) ? 'we' : 'wd';
      let v = P[i].v, w = 1;
      if (cumulative) { const dt = (P[i + 1].t - P[i].t) / 36e5; v = P[i + 1].v - P[i].v; w = dt; if (dt <= 0 || dt > 6 || v < 0) continue; }
      add('all', h, v, w); add(k, h, v, w); if (k === 'we') nWe += 1;
    }
  }
  const all = fill24(...B.all);
  if (!all) throw new Error('gaps');
  const wd = fill24(...B.wd), we = nWe ? fill24(...B.we) : null, mean = (a) => a.reduce((x, v) => x + v, 0) / 24;
  const split = !!(wd && we && mean(wd) > 0);
  return { prof: split ? wd : all, profWe: split ? we : null, weF: split ? +(mean(we) / mean(wd)).toFixed(3) : null, n: pts.length, days, cumulative };
}

// pressure logger -> bar; unit from the header (mbar, kPa, m WS, psi), else bar
const unitOf = (head) => { const h = head.toLowerCase(); if (/mbar/.test(h)) return 0.001; if (/kpa/.test(h)) return 0.01; if (/psi/.test(h)) return 0.0689476; if (/\bm\s?ws|mwc|\bm\b.*wasser|wassers/.test(h)) return 0.0980665; return 1; };
export async function loggerFromFile(file) {
  const { pts, head } = await readSeries(file), u = unitOf(head), T = pts.filter((p) => p.t != null);
  if (T.length < 6) throw new Error('few');
  const z = () => [Array(24).fill(0), Array(24).fill(0)], B = { wd: z(), we: z() };
  T.forEach((p) => { const k = isWe(p.t) ? 'we' : 'wd', h = new Date(p.t).getHours(); B[k][0][h] += p.v * u; B[k][1][h] += 1; });
  const agg = (k) => B[k][0].map((s, h) => (B[k][1][h] ? +(s / B[k][1][h]).toFixed(3) : null));
  T.sort((a, b) => a.t - b.t);
  return { file: file.name, n: T.length, from: T[0].t, to: T[T.length - 1].t, unit: u, wd: agg('wd'), we: agg('we'), nwd: B.wd[1], nwe: B.we[1] };
}

// logger value for the measuring time: mean of that hour (or of the whole day type) over all recorded days
export function loggerValue(lg, day, hour) {
  const k = day === 'we' ? 'we' : 'wd', v = lg[k], n = day === 'we' ? lg.nwe : lg.nwd;
  if (hour != null && hour !== '') return v[+hour];
  const tot = n.reduce((a, c) => a + c, 0);
  return tot ? +(v.reduce((a, x, h) => a + (x ?? 0) * n[h], 0) / tot).toFixed(3) : null;
}

// file name -> hydrant (e.g. "H12.csv", "Logger_Hydrant H12_2026-05.xlsx")
export function matchHydrant(fileName, hydrants) {
  const nz = (x) => x.replace(/(^|\D)0+(\d)/g, '$1$2'), base = fileName.replace(/\.[^.]+$/, '').toLowerCase(), flat = nz(base.replace(/[^a-z0-9äöüß]/g, ''));
  let best = null, bl = 0;
  hydrants.forEach((h) => {
    const nm = (h.name || '').toLowerCase(), keys = [nm.replace(/[^a-z0-9äöüß]/g, ''), nm.replace(/hydrant/g, '').replace(/[^a-z0-9äöüß]/g, ''), String(h.id).toLowerCase()].map(nz);
    const tok = nm.match(/[a-z]*\d+/g)?.pop();
    keys.forEach((k) => { if (k.length >= 2 && flat.includes(k) && k.length > bl) { best = h; bl = k.length; } });
    if (tok && !bl && new RegExp(`(^|[^a-z0-9])${tok}([^0-9]|$)`).test(base)) { best = h; bl = tok.length; }
  });
  return best;
}
